import { Form } from "antd";

// service calls
import { getActiveOffers, bulkOfferIssuance } from "services/api/offerIssuance";
// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants and types
import { FormInstance } from "antd/es/form/Form";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { DateTimeFormats } from "constants/global";

// for dayjs

import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";

dayjs.extend(weekday);
dayjs.extend(localeData);

type AdhocReportPropTypes = {
	OfferIssuanceSubmit: () => void;
	loading: boolean;
	form: FormInstance;
	cardNumber?: string;
	cardNumberStart?: string;
	cardNumberEnd?: string;
	errorCardStart: boolean;
	errorCardEnd: boolean;
	member?: boolean;
	quantity?: number;
	offer?: string;
	remarks?: string;
	offerSelect: SelectObject[];
	errorStat: boolean;
	errorStartDate: boolean;
	errorEndDate: boolean;
	errorOffer: boolean;
	errorQuantity: boolean;
	errorCardNumber: boolean;
	memberChange: (e: any) => void;
	startDate?: string;
	endDate?: string;
	errorMessageQuantity?: string;
	errorMessageCardNumber?: string;
	errorMessageCardStart?: string;
	errorMessageCardEnd?: string;
	handleStartDate: (s: string) => void;
	handleOffer: (s: SelectObject) => void;
	handleRemarks: (s: string) => void;
	handleCardNumber: (s: string) => void;
	handleCardNumberStart: (s: string) => void;
	handleCardNumberEnd: (s: string) => void;
	handleQuantity: (s: number) => void;
	handleEndDate: (s: string) => void;
	handleClearAll: (s: string) => void;
};

interface SelectObject {
	label: string;
	value: string;
	key: string;
}

export function useOfferIssuancePage(): AdhocReportPropTypes {
	const [form] = Form.useForm();

	const [loading] = useState<boolean>(false);
	const [member, setMember] = useState<boolean>(true);
	const [cardNumber, setCardNumber] = useState<string>();
	const [errorMessageQuantity, setErrorMessageQuantity] = useState<string>("");
	const [errorMessageCardNumber, setErrorMessageCardNumber] = useState<string>("");
	const [cardNumberStart, setCardNumberStart] = useState<string>();
	const [cardNumberEnd, setCardNumberEnd] = useState<string>();
	const [quantity, setQuantity] = useState<number>(0);
	const [offer, setOffer] = useState<string>();
	const [startDate, setStartDate] = useState<string>();
	const [endDate, setEndDate] = useState<string>();
	const [remarks, setRemarks] = useState<string>();
	const [offerSelect, setOfferSelect] = useState<SelectObject[]>([]);
	const [errorStat, setErrorStat] = useState<boolean>(false);
	const [errorCardNumber, setErrorCardNumber] = useState<boolean>(false);
	const [errorStartDate, setErrorStartDate] = useState<boolean>(false);
	const [errorQuantity, setErrorQuantity] = useState<boolean>(false);
	const [errorEndDate, setErrorEndDate] = useState<boolean>(false);
	const [errorOffer, setErrorOffer] = useState<boolean>(false);
	const [errorCardStart, setErrorCardStart] = useState<boolean>(false);
	const [errorCardEnd, setErrorCardEnd] = useState<boolean>(false);
	const [errorMessageCardStart, setErrorMessageCardStart] = useState<string>("");
	const [errorMessageCardEnd, setErrorMessageCardEnd] = useState<string>("");

	useEffect(() => {
		const callOffer = async () => {
			const { data: reportsData } = await getActiveOffers();
			const { errors, data } = reportsData;

			const offerData: SelectObject[] = [];
			if (errors.length > 0) {
				return;
			}
			data.map((res: any, i: number) => {
				offerData.push({
					...res,
					label: res.voucher_display_title,
					value: res.offer_id,
					key: `${res.internal_voucher_type_code}-${i}`
				});
			});

			setOfferSelect(offerData);
		};
		callOffer();
	}, []);

	const memberChange = useCallback(
		(val: any) => {
			setMember(val.target.value);
		},
		[member]
	);
	const handleCardNumber = useCallback(
		(val: string) => {
			const splitted = val.split(",");
			setCardNumber(val);
			if (!val || val === "") {
				setErrorCardNumber(true);
				setErrorMessageCardNumber("this fields is required");
				return;
			}

			if (splitted.length > 2000) {
				setErrorCardNumber(true);
				setErrorMessageCardNumber("CR Card number entry must not be more than 2000.");
				return;
			}
			setErrorCardNumber(false);
		},
		[cardNumber, errorCardNumber, errorMessageCardNumber]
	);
	const validateCardNumberRange = (start: string | undefined, end: string | undefined) => {
		let isValid = true;
		if (!start) {
			setErrorCardStart(true);
			setErrorMessageCardStart("This field is required.");
			isValid = false;
		} else if (Number(start) === 0 && Number(end) === 0) {
			setErrorCardStart(true);
			setErrorCardEnd(true);
			setErrorMessageCardStart("Start Range and End Range cannot both be 0.");
			setErrorMessageCardEnd("Start Range and End Range cannot both be 0.");
			isValid = false;
		} else if (Number(start) > Number(end)) {
			setErrorCardStart(true);
			setErrorCardEnd(true);
			setErrorMessageCardStart("Start Range must not be greater than End Range.");
			setErrorMessageCardEnd("End Range must be greater than Start Range.");
			isValid = false;
		} else if (Number(end) - Number(start) > 2000) {
			setErrorCardEnd(true);
			setErrorMessageCardEnd("CR Card number series must not be greater than 2000.");
			isValid = false;
		} else {
			setErrorCardStart(false);
			setErrorCardEnd(false);
		}
		return isValid;
	};

	const handleCardNumberStart = useCallback(
		(num: string) => {
			setCardNumberStart(num);
			validateCardNumberRange(num, cardNumberEnd);
		},
		[cardNumberEnd]
	);

	const handleCardNumberEnd = useCallback(
		(num: string) => {
			setCardNumberEnd(num);
			validateCardNumberRange(cardNumberStart, num);
		},
		[cardNumberStart]
	);

	const handleQuantity = useCallback(
		(num: number) => {
			setQuantity(num);
			if (!num) {
				setErrorQuantity(true);
				setErrorMessageQuantity("This field is required");
			} else if (num <= 0) {
				setErrorQuantity(true);
				setErrorMessageQuantity("Quantity should be greater than 0");
			} else if (num > 200) {
				setErrorQuantity(true);
				setErrorMessageQuantity("Quantity must not be greater than 200");
			} else {
				setErrorQuantity(false);
			}
		},
		[quantity, setQuantity]
	);

	const handleStartDate = useCallback(
		(dateString: string) => {
			setStartDate(dateString);
		},
		[startDate]
	);
	const handleRemarks = useCallback(
		(val: any) => {
			setRemarks(val);
		},
		[remarks, setRemarks]
	);
	const updateFields = (start: string, end: string) => {
		setStartDate(start);
		setEndDate(end);
		form.setFieldValue("startDate", start ? dayjs(start, DateTimeFormats.USE_YYYY_MM_DD) : "");
		form.setFieldValue("endDate", end ? dayjs(end, DateTimeFormats.USE_YYYY_MM_DD) : "");
	};

	const handleOffer = useCallback(
		async (val: SelectObject) => {
			setOffer(val as any);
			const { data: reportsData } = await getActiveOffers();
			const { data } = reportsData;
			const selectedOffer = data.find((e: any) => e.offer_id === val);

			updateFields(selectedOffer?.redemption_start_date, selectedOffer?.redemption_end_date);
		},
		[form]
	);

	const handleEndDate = useCallback(
		(dateString: string) => {
			setEndDate(dateString);
		},
		[endDate]
	);

	const resetFields = () => {
		setCardNumberEnd("");
		setCardNumberStart("");
		setCardNumber("");
		setQuantity(0);
		setStartDate("");
		setEndDate("");
		setRemarks("");
		setErrorStartDate(false);
		setErrorEndDate(false);
		setErrorQuantity(false);
		setErrorOffer(false);
		setOffer("");
		setErrorCardNumber(false);
		setErrorCardStart(false);
		setErrorCardEnd(false);

		form.resetFields();
		setErrorStat(false);
	};

	const handleClearAll = useCallback(
		(message?: string) => {
			form.resetFields();
			resetFields();

			showModal(message || "All Fields has been cleared");
		},
		[resetFields]
	);

	const showModal = (title: string, error = false) => {
		CountDownModal({
			timer: 2,
			title,
			errorIcon: error
		});
	};

	const memberCondition = async (errorMessages: string[], trimmedCardNumber: [], maxCardNumber: number) => {
		if (member) {
			// Set error messages and flags based on the conditions
			if (!cardNumber) {
				setErrorCardNumber(true);
				setErrorMessageCardNumber("This field is required.");
				errorMessages.push("CR Card number is required.");
			}
			if (trimmedCardNumber.length > maxCardNumber) {
				setErrorCardNumber(true);
				setErrorMessageCardNumber("CR Card number entry must not be more than 2000.");
				errorMessages.push("CR Card number entry must not be more than 2000.");
			}
		}
	};

	const nonMemberCondition = async (errorMessages: string[], maxCardNumber: number) => {
		if (!member) {
			// Check for required fields and range validation
			const validations = [];

			if (!cardNumberStart) {
				validations.push({
					stateSetter: setErrorCardStart,
					errorMessage: "This field is required.",
					errorMessagePush: "CR Card number Start Range is required."
				});
			}

			if (!cardNumberEnd) {
				validations.push({
					stateSetter: setErrorCardEnd,
					errorMessage: "This field is required.",
					errorMessagePush: "CR Card number End Range is required."
				});
			}

			if (Number(cardNumberStart) > Number(cardNumberEnd)) {
				validations.push(
					{
						stateSetter: setErrorCardStart,
						errorMessage: "Start Range must not be greater than End Range.",
						errorMessagePush: "CR Card Number Start Range must not be greater than CR Card Number End Range"
					},
					{
						stateSetter: setErrorCardEnd,
						errorMessage: "End Range must be greater than Start Range.",
						errorMessagePush: ""
					}
				);
			}

			if (Number(cardNumberEnd) - Number(cardNumberStart) > maxCardNumber) {
				validations.push({
					stateSetter: setErrorCardEnd,
					errorMessage: "CR Card number series must not be greater than 2000.",
					errorMessagePush: "CR Card number series must not be greater than 2000."
				});
			}

			// Apply validations
			validations.forEach(({ stateSetter, errorMessage, errorMessagePush }) => {
				stateSetter(true);
				if (errorMessagePush) {
					errorMessages.push(errorMessagePush);
				}
				setErrorMessageCardStart(errorMessage); // Only set Start Range error message once
			});
		}
	};
	const quantityCondition = async (errorMessages: string[], maxQuantity: number) => {
		if (!quantity || quantity <= 0) {
			setErrorQuantity(true);
			if (!quantity) {
				setErrorMessageQuantity("This field is required");
				errorMessages.push("Quantity is required.");
			} else {
				setErrorMessageQuantity("Quantity should be greater than 0");
				errorMessages.push("Quantity should be greater than 0");
			}
		} else if (quantity > maxQuantity) {
			setErrorQuantity(true);
			setErrorMessageQuantity("Quantity must not be greater than 200");
			errorMessages.push("Quantity must not be greater than 200");
		}
	};
	const OfferIssuanceSubmit = async () => {
		const showModal = (title: string, error = false) => {
			CountDownModal({
				timer: 3,
				title,
				errorIcon: error
			});
		};
		const splittedCardNumber = cardNumber?.split(",");
		const trimmedCardNumber: string[] = (splittedCardNumber || []).map((str) => str.trim().replace(/\n/g, ""));

		const maxQuantity = 200;
		const maxCardNumber = 2000;

		const errorMessages: string[] = [];

		await memberCondition(errorMessages, trimmedCardNumber as [], maxCardNumber);
		await nonMemberCondition(errorMessages, maxCardNumber);

		if (!offer) {
			setErrorOffer(true);
			errorMessages.push("Offer is required.");
		}

		await quantityCondition(errorMessages, maxQuantity);
		if (endDate && !startDate) {
			setErrorStartDate(true);
			errorMessages.push("Start date is required when End date is provided.");
		}
		if (startDate && !endDate) {
			setErrorEndDate(true);
			errorMessages.push("End date is required when Start date is provided.");
		}
		if (!remarks) {
			setErrorStat(true);
			errorMessages.push("Remarks are required.");
		}

		// Show modal with collected error messages
		if (errorMessages.length > 0) {
			showModal(errorMessages[0], true);
			return;
		}

		try {
			const splittedCardNumber = cardNumber?.split(",");
			const trimmedCardNumber = splittedCardNumber?.map((str) => str.trim().replace("\n", ""));

			const payload = {
				member,
				card_numbers: trimmedCardNumber,
				card_start_range: cardNumberStart,
				card_end_range: cardNumberEnd,
				quantity_of_offers: quantity,
				start_date: startDate,
				end_date: endDate,
				offer,
				remarks
			};
			const { data: reportsData } = await bulkOfferIssuance(payload);
			const { errors, data } = reportsData;

			if (!data) {
				showModal(`Error : ${errors[0].message}`, true);
				return;
			}

			showModal("Offer Issuance Successfully Submitted");
			resetFields();
			form.resetFields();
			setErrorStat(false);
		} catch (error: any) {
			showModal(`Something went wrong : ${error.response.data.errors[0].message}`, true);
		}
	};

	return {
		loading,
		form,
		member,
		cardNumber,
		cardNumberStart,
		cardNumberEnd,
		quantity,
		startDate,
		endDate,
		offer,
		offerSelect,
		remarks,
		errorStat,
		errorMessageCardNumber,
		errorCardNumber,
		errorCardStart,
		errorCardEnd,
		errorMessageCardStart,
		errorMessageCardEnd,
		errorStartDate,
		errorEndDate,
		errorQuantity,
		errorMessageQuantity,
		errorOffer,
		handleCardNumber,
		handleRemarks,
		handleCardNumberStart,
		handleCardNumberEnd,
		handleQuantity,
		memberChange,
		handleOffer,
		OfferIssuanceSubmit,
		handleStartDate,
		handleEndDate,
		handleClearAll
	};
}
